import { capitalize } from "lodash";
import type { FC } from "react";
import { Col, Row } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import TagManager from "react-gtm-module";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { GetDashboardDataQuery } from "api/generated";

import IconBook from "components/Icons/IconBook/IconBook";
import { CalendarWeek } from "features";

import StrawberryIcon from "images/icons/checkberry.svg";
import SnailIcon from "images/icons/drakusnail.svg";

import { useAppSelector } from "store/hooks";
import { getGrade } from "store/slices/user";

import { useContentAccess } from "utils/hooks/useContentAccess";
import { useSubjects } from "utils/hooks/useSubjects";
import { useWeekData } from "utils/hooks/useWeekData";

interface Props {
  data: GetDashboardDataQuery | undefined;
}

const DashboardCalendarOverview: FC<Props> = ({ data }) => {
  const { t } = useTranslation(["dashboard", "exercise"]);
  const navigate = useNavigate();
  const { isTrainingFinished, isTrainingPartiallyDone, weekData } = useWeekData(data);
  const { rawData: subjects } = useSubjects();
  const { tutoringVisible } = useContentAccess();
  const grade = useAppSelector(getGrade);
  const isGraduate = grade === "maturant";

  const redirectExercise = () => {
    TagManager.dataLayer({ dataLayer: { event: "start-exercise" } });

    navigate("/cviceni");
  };

  const getTexts = () => {
    if (isTrainingFinished) {
      return {
        Icon: StrawberryIcon,
        heading: t("training.done.heading"),
        // Graduates have no tutoring (see tutoringVisible below), so the finished-training
        // text invites them to video reviews instead of tutoring. [TD-430]
        description: t("training.done.description", { context: isGraduate ? "graduate" : undefined }),
      };
    }

    if (isTrainingPartiallyDone) {
      return {
        Icon: SnailIcon,
        heading: t("training.partially.heading"),
        description: t("training.partially.description"),
      };
    }

    return {
      Icon: SnailIcon,
      heading: t("training.notStarted.heading"),
      description: t("training.notStarted.description"),
    };
  };

  const { Icon, heading, description } = getTexts();

  return (
    <div className="well">
      <div className="pt-6px">
        <div className="px-3 pt-8px">
          <CalendarWeek data={weekData} />
        </div>
      </div>
      <hr />
      <div className="pt-12px px-16px px-sm-28px pb-32px">
        <Row className="g-12px">
          <Col xs={"auto"} style={{ width: 50 }} className={"text-center"}>
            <Icon />
          </Col>
          <Col>
            <p className={"fw-bold mb-8px"}>{heading}</p>
            <p className={"mb-12px"}>{description}</p>
          </Col>
        </Row>
        <Row>
          <Col xs={"auto"} style={{ width: 50 }} className={"d-none d-sm-block"}></Col>
          <Col>
            {isTrainingFinished ? (
              <div className="d-flex gap-2 flex-wrap">
                {/* AIDEV-NOTE: Maturant doučování nemá — tlačítka na `/doucovani/*` by ho vrátila
                    zpátky guardem (Nález 1, TD-351) a bez podmínky by vypadala jako rozbitá appka. */}
                {tutoringVisible &&
                  subjects?.map(subject => {
                    if (!subject.slug) return null;

                    return (
                      <a
                        href={`/doucovani/${subject.slug}`}
                        className="btn btn-azure d-flex align-items-center"
                        key={subject.appAbbr}
                      >
                        <IconBook width="20" height="20" className={"me-2 icon"} />
                        {t("trainSubject", { subject: subject.appAbbr, ns: "tutoring" })}
                      </a>
                    );
                  })}
                <a href="/ulohy" className="btn btn-transparent">
                  {t("allAssignments", { ns: "common" })}
                </a>
              </div>
            ) : isTrainingPartiallyDone ? (
              <a href="/cviceni" className="btn btn-primary">
                {capitalize(t("btn.finishTraining", { ns: "exercise" }))}
              </a>
            ) : (
              <Button onClick={redirectExercise}>{capitalize(t("btn.startExercise", { ns: "exercise" }))}</Button>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DashboardCalendarOverview;
